import {
  accordionClasses,
  accordionDetailsClasses,
  accordionSummaryClasses,
  typographyClasses,
} from '@mui/material';
import type { AccordionProps } from '@mui/material/Accordion';
import { Theme } from '@mui/material/styles';
import { COLORS_SCHEMA } from '../../palette';

// NEW VARIANT
declare module '@mui/material/Paper' {
  interface PaperOwnProps {
    compact?: boolean;
  }
  interface PaperPropsVariantOverrides {
    soft: true;
    filled: true;
  }
}

export default function Accordion(theme: Theme) {
  const rootStyles = (ownerState: AccordionProps) => {
    const outlinedVariant = ownerState.variant === 'outlined';
    const softVariant = ownerState.variant === 'soft';
    const filledVariant = ownerState.variant === 'filled';
    const elevationVariant = ownerState.variant === 'elevation';

    const borderRadiusValue = ownerState.square ? 0 : theme.spacing(0.5);

    const defaultStyle = {
      borderRadius: borderRadiusValue,
      // Override MUI Paper's :first-of-type and :last-of-type border-radius
      '&:first-of-type': {
        borderTopLeftRadius: borderRadiusValue,
        borderTopRightRadius: borderRadiusValue,
      },
      '&:last-of-type': {
        borderBottomLeftRadius: borderRadiusValue,
        borderBottomRightRadius: borderRadiusValue,
      },
      // '&:before': {
      //   display: 'none',
      // },
      [`&.${accordionClasses.expanded}`]: {
        boxShadow: elevationVariant ? theme.customShadows.z8 : 'none',
        borderRadius: borderRadiusValue,
        margin: 'initial',
        '&:first-of-type': {
          borderTopLeftRadius: borderRadiusValue,
          borderTopRightRadius: borderRadiusValue,
        },
        '&:last-of-type': {
          borderBottomLeftRadius: borderRadiusValue,
          borderBottomRightRadius: borderRadiusValue,
        },
      },
      [`&.${accordionClasses.disabled}`]: {
        backgroundColor: 'transparent',
      },
      [`.${accordionSummaryClasses.root}`]: {
        paddingRight: theme.spacing(1),
        paddingLeft: theme.spacing(1),
        minHeight: theme.spacing(ownerState.compact ? 4 : 5.5),
        [`&.${accordionSummaryClasses.disabled}`]: {
          opacity: 1,
          color: theme.palette.action.disabled,
          [`.${typographyClasses.root}`]: {
            color: 'inherit',
          },
        },
        [`.${accordionSummaryClasses.content}`]: {
          margin: theme.spacing(ownerState.compact ? 0 : 1.5, 0),
        },
        [`&.${accordionSummaryClasses.expanded}`]: {
          minHeight: theme.spacing(ownerState.compact ? 4 : 5.5),
          [`.${accordionSummaryClasses.content}`]: {
            margin: theme.spacing(ownerState.compact ? 0 : 1, 0),
          },
        },
      },
      [`.${accordionSummaryClasses.expandIconWrapper}`]: {
        color: 'inherit',
        marginLeft: theme.spacing(1),
      },
      [`.${accordionDetailsClasses.root}`]: {
        padding: theme.spacing(ownerState.compact ? 0 : 1),
      },
    };

    const colorStyle = Object.values(COLORS_SCHEMA).map((color) => ({
      ...(ownerState.color === color && {
        backgroundColor: theme.palette.background.paper,

        // FILLED
        ...(filledVariant && {
          backgroundColor: theme.palette[color][200],
        }),

        // OUTLINED
        ...(outlinedVariant && {
          border: `solid 1px ${theme.palette[color][300]}`,
        }),

        // SOFT
        ...(softVariant && {
          backgroundColor: 'transparent',
          border: 'none',
        }),
      }),
    }));

    return [defaultStyle, ...colorStyle];
  };

  return {
    MuiAccordion: {
      defaultProps: {
        variant: 'elevation',
      },
      styleOverrides: {
        root: ({ ownerState }: { ownerState: AccordionProps }) =>
          rootStyles(ownerState),
      },
    },
  };
}
